import { type ReactNode } from 'react';
import { Pencil } from 'lucide-react';

// Shared <th> role-column header used by the access matrices in
// MobileAccessTab and WebAccessPage. Both pages inlined this exact markup:
// the role name + an edit (Pencil) button, a pill count label, and an
// optional "N users" line. The only structural difference was that the web
// page rendered an extra delete button inside the action row — exposed here
// as the optional `extraActions` slot so a site that passes nothing renders
// exactly as before.
interface RoleColumnHeaderCellProps {
  /** Role display name shown in the header. Passed verbatim from each site. */
  name: string;
  /** Fired when the edit (Pencil) button is clicked. Each site passes its own
   *  `() => openEditRole(role)` closure verbatim. */
  onEdit: () => void;
  /** The pill count label content, e.g. `{getFeatureCount(role.id)} features`
   *  (mobile) or `{getPermissionCount(role.id)} access` (web). */
  countLabel: ReactNode;
  /** Number of users assigned to the role; the "N user(s)" line only renders
   *  when this is > 0, matching both sites' original guard. */
  userCount?: number;
  /** Optional extra action buttons rendered after the edit button inside the
   *  action row (web's conditional delete button). Sites that omit it render
   *  exactly as before. */
  extraActions?: ReactNode;
  /** Tailwind gap class for the action row. Defaults to the mobile value
   *  `gap-1.5`; the web site passes `gap-2`. */
  actionRowGapClassName?: string;
}

export function RoleColumnHeaderCell({
  name,
  onEdit,
  countLabel,
  userCount,
  extraActions,
  actionRowGapClassName = 'gap-1.5',
}: RoleColumnHeaderCellProps) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`flex items-center ${actionRowGapClassName}`}>
        <span className="text-sm font-semibold text-primary-600">{name}</span>
        <button
          onClick={onEdit}
          className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
          title="Edit role"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
        {extraActions}
      </div>
      <span className="text-xs text-gray-400 font-normal bg-gray-100 px-2 py-0.5 rounded-full">
        {countLabel}
      </span>
      {(userCount || 0) > 0 && (
        <span className="text-xs text-blue-500 font-normal">
          {userCount} user{(userCount || 0) !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

export default RoleColumnHeaderCell;
